import React, { useState } from 'react'
import { NavLink, Outlet } from 'react-router-dom'
import {
  HiOutlineBuildingOffice2,
  HiOutlineCircleStack,
  HiOutlineCpuChip,
  HiOutlineEnvelope,
  HiOutlinePuzzlePiece,
  HiOutlineRectangleStack,
  HiOutlineTag,
  HiOutlineWallet,
  HiBars3,
  HiMoon,
  HiSun,
  HiArrowRightOnRectangle,
  HiChevronLeft,
  HiChevronRight,
} from 'react-icons/hi2'
import { useAuth } from '../hooks/useAuth'
import { useTheme } from '../contexts/ThemeContext'
import AdSagaLogo from '../assets/adsaga_logo.svg'
import { cn } from '../components/ui/cn'

const SIDEBAR_COLLAPSED_KEY = 'admin.sidebar.collapsed'

const navGroups = [
  {
    label: 'Catalog',
    items: [
      { name: 'Models', href: '/admin/models', icon: HiOutlineCpuChip },
      { name: 'Service Providers', href: '/admin/service-providers', icon: HiOutlinePuzzlePiece },
      { name: 'Currency', href: '/admin/currency', icon: HiOutlineCircleStack },
    ],
  },
  {
    label: 'Billing',
    items: [
      { name: 'Credit Maintenance', href: '/admin/credit-maintenance', icon: HiOutlineWallet },
      { name: 'Pricing', href: '/admin/pricing-management', icon: HiOutlineTag },
      { name: 'Subscriptions', href: '/admin/subscription-management', icon: HiOutlineRectangleStack },
    ],
  },
  {
    label: 'Customers',
    items: [
      { name: 'Contact leads', href: '/admin/contact-leads', icon: HiOutlineEnvelope },
      { name: 'Organisations', href: '/admin/organisations', icon: HiOutlineBuildingOffice2 },
    ],
  },
]

const AdminLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1'
    } catch {
      return false
    }
  })
  const { admin, logout } = useAuth()
  const { isDark, toggleTheme } = useTheme()

  const toggleCollapsed = () => {
    setCollapsed((prev) => {
      const next = !prev
      try {
        localStorage.setItem(SIDEBAR_COLLAPSED_KEY, next ? '1' : '0')
      } catch {
        /* ignore */
      }
      return next
    })
  }

  const sidebarWidth = collapsed ? 'lg:w-[4.5rem]' : 'lg:w-64'
  const mainMargin = collapsed ? 'lg:ml-[4.5rem]' : 'lg:ml-64'

  const navLinkClass = ({ isActive }) =>
    cn('sidebar-item w-full', isActive && 'active', collapsed && 'justify-center px-2')

  return (
    <div className="min-h-screen bg-app dark:bg-app-dark">
      {sidebarOpen && (
        <button
          type="button"
          className="fixed inset-0 z-40 bg-secondary-900/50 lg:hidden"
          aria-label="Close menu"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 flex flex-col border-r border-muted bg-card shadow-sm transition-all duration-300 dark:border-muted-dark dark:bg-card-dark',
          sidebarWidth,
          sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        )}
      >
        <div className="flex h-16 items-center justify-between gap-2 border-b border-muted px-3 dark:border-muted-dark">
          <div className={cn('flex min-w-0 items-center gap-2', collapsed && 'justify-center w-full')}>
            <img src={AdSagaLogo} alt="AdSaga" className="h-9 w-9 shrink-0 object-contain" />
            {!collapsed && (
              <div className="min-w-0">
                <p className="truncate text-sm font-bold text-secondary-900 dark:text-secondary-50">AdSaga Admin</p>
                <p className="truncate text-xs text-text-muted dark:text-text-muted-dark">Platform control</p>
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={() => setSidebarOpen(false)}
            className="rounded-lg p-1 text-secondary-500 hover:bg-secondary-100 lg:hidden dark:hover:bg-secondary-800"
            aria-label="Close sidebar"
          >
            <HiChevronLeft className="h-5 w-5" />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto px-2 py-4">
          {navGroups.map((group) => (
            <div key={group.label} className="mb-4">
              {!collapsed && (
                <p className="mb-1 px-3 text-xs font-semibold uppercase tracking-wider text-text-muted dark:text-text-muted-dark">
                  {group.label}
                </p>
              )}
              <ul className="space-y-0.5">
                {group.items.map((item) => (
                  <li key={item.href}>
                    <NavLink
                      to={item.href}
                      end={item.href === '/admin/credit-maintenance' ? false : undefined}
                      className={navLinkClass}
                      onClick={() => setSidebarOpen(false)}
                      title={collapsed ? item.name : undefined}
                    >
                      <item.icon className="h-5 w-5 shrink-0" aria-hidden />
                      {!collapsed && <span className="truncate">{item.name}</span>}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>

        <div className="hidden border-t border-muted p-2 lg:block dark:border-muted-dark">
          <button
            type="button"
            onClick={toggleCollapsed}
            className="sidebar-item w-full justify-center"
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? <HiChevronRight className="h-5 w-5" /> : <HiChevronLeft className="h-5 w-5" />}
            {!collapsed && <span>Collapse</span>}
          </button>
        </div>

        <div className="border-t border-muted p-3 dark:border-muted-dark">
          {!collapsed && (
            <div className="mb-2 flex items-center gap-2 px-1">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary-100 text-sm font-semibold text-primary-700 dark:bg-primary-900/40 dark:text-primary-300">
                {admin?.fullname?.charAt(0) || 'A'}
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-secondary-900 dark:text-secondary-100">{admin?.fullname}</p>
                <p className="truncate text-xs text-text-muted dark:text-text-muted-dark">{admin?.email}</p>
              </div>
            </div>
          )}
          <button
            type="button"
            onClick={() => logout()}
            className={cn('sidebar-item w-full text-red-600 hover:bg-red-50 hover:text-red-700 dark:hover:bg-red-950/30', collapsed && 'justify-center')}
            title={collapsed ? 'Logout' : undefined}
          >
            <HiArrowRightOnRectangle className="h-5 w-5 shrink-0" />
            {!collapsed && <span>Logout</span>}
          </button>
        </div>
      </aside>

      <div className={cn('flex min-h-screen flex-col transition-[margin] duration-300', mainMargin)}>
        <header className="sticky top-0 z-30 flex h-14 items-center justify-between gap-4 border-b border-muted bg-card/95 px-4 backdrop-blur dark:border-muted-dark dark:bg-card-dark/95 lg:px-6">
          <div className="flex items-center gap-3">
            <button
              type="button"
              className="rounded-lg p-2 text-secondary-600 hover:bg-secondary-100 lg:hidden dark:text-secondary-300 dark:hover:bg-secondary-800"
              onClick={() => setSidebarOpen(true)}
              aria-label="Open menu"
            >
              <HiBars3 className="h-6 w-6" />
            </button>
            <p className="text-sm font-medium text-secondary-600 dark:text-secondary-400 lg:hidden">AdSaga Admin</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={toggleTheme}
              className="rounded-lg p-2 text-secondary-600 transition-colors hover:bg-secondary-100 dark:text-secondary-300 dark:hover:bg-secondary-800"
              aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {isDark ? <HiSun className="h-5 w-5" /> : <HiMoon className="h-5 w-5" />}
            </button>
            <div className="hidden items-center gap-2 sm:flex">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-100 text-xs font-semibold text-primary-700 dark:bg-primary-900/40 dark:text-primary-300">
                {admin?.fullname?.charAt(0) || 'A'}
              </div>
              <span className="max-w-[140px] truncate text-sm text-secondary-700 dark:text-secondary-200">{admin?.fullname}</span>
            </div>
          </div>
        </header>

        <main className="flex-1 px-4 py-6 lg:px-8 lg:py-8">
          <Outlet />
        </main>
      </div>
    </div>
  )
}

export default AdminLayout
